import { z } from "zod";
// Reuse the canonical Json schema from the shared barrel's first module so
// `export *` from index.ts has exactly one `jsonSchema` symbol.
import { jsonSchema } from "./service-requests";

/**
 * Maintenance shared contract (Strangler-Fig migration, manifest-6 / Track A6).
 *
 * Single source of truth for the `maintenance_requests` + `maintenance_assignments`
 * wire shape shared by the web SPA (`src/repositories/maintenance.ts`) and the
 * NestJS slice (`apps/api/src/maintenance/*`). Shape is **snake_case** so the
 * frontend<->Nest transport is a field-for-field swap (no renames), matching the
 * Supabase generated Rows and the Nest repository `.select()` lists.
 *
 * The web repo `.parse()`s Nest responses against these schemas at the staff
 * flip boundary, so any backend/frontend drift surfaces at runtime (and the repo
 * falls back to the Supabase path).
 *
 * ⚠️ TOLERANCE RULE (the #1699 silent-no-op lesson): the staff list/detail reads
 * embed nested `property` / `unit` / `tenant` relations (and the assignment read a
 * `provider` relation) whose column sets vary and may be `null`. So relation
 * fields are `.optional()` + `.nullable()` with a **passthrough** shape — a
 * too-strict relation schema would make every Nest call silently fall back to
 * Supabase. Only each table's own columns are pinned tightly, and the whole
 * object is `.passthrough()` so a new migration column never rejects a payload.
 *
 * BE adoption note: `apps/api` does not yet import this package — its maintenance
 * slice keeps a class-validator DTO (already shape-aligned). Wiring Nest to
 * consume `@dallali/shared` needs the dual ESM/CJS build step tracked in
 * `docs/migration/domain-debugging-matrix.md`.
 */

/** Loose, passthrough relation shape — see the tolerance rule above. */
const relation = z.record(jsonSchema).nullable().optional();

/**
 * Lifecycle statuses (advisory — the row schema keeps `status` an open string so
 * a new status value never rejects server data).
 */
export const MAINTENANCE_REQUEST_STATUSES = [
  "pending",
  "new",
  "assigned",
  "in_progress",
  "on_hold",
  "completed",
  "done",
  "closed",
  "cancelled",
  "rejected",
] as const;
export const maintenanceRequestStatusSchema = z.enum(MAINTENANCE_REQUEST_STATUSES);
export type MaintenanceRequestStatus = (typeof MAINTENANCE_REQUEST_STATUSES)[number];

/** Groups for aggregate stats — mirrors the bins used in maintenance-aggregate.service + FE repository. */
export const MAINTENANCE_COMPLETED_STATUSES: MaintenanceRequestStatus[] = ["completed", "done", "closed"];
export const MAINTENANCE_PENDING_STATUSES: MaintenanceRequestStatus[] = ["pending", "new", "assigned", "in_progress", "on_hold"];
export const MAINTENANCE_CANCELLED_STATUSES: MaintenanceRequestStatus[] = ["cancelled", "rejected"];

/**
 * A persisted maintenance request as the staff dashboard consumes it. Own-column
 * field set + nullability mirror the Supabase generated `maintenance_requests`
 * Row; the nested `property` / `unit` / `tenant` joins are appended by the staff
 * list/detail reads (loose + optional + nullable).
 */
export const maintenanceRequestSchema = z
  .object({
    id: z.string(),
    company_id: z.string(),
    property_id: z.string().nullable(),
    unit_id: z.string().nullable(),
    tenant_id: z.string().nullable(),
    owner_id: z.string().nullable().optional(),
    title: z.string(),
    description: z.string().nullable(),
    category: z.string(),
    status: z.string(),
    priority: z.string(),
    // `images` is a Json column (string[] in practice); keep it Json-loose.
    images: jsonSchema.nullable().optional(),
    notes: z.string().nullable(),
    estimated_cost: z.number().nullable().optional(),
    owner_approval_required: z.boolean().nullable().optional(),
    owner_approved: z.boolean().nullable().optional(),
    owner_approved_at: z.string().nullable().optional(),
    created_at: z.string(),
    updated_at: z.string(),
    // Joined relations (staff list/detail) — loose + optional + nullable.
    property: relation,
    unit: relation,
    tenant: relation,
  })
  .passthrough();

export type MaintenanceRequest = z.infer<typeof maintenanceRequestSchema>;

/**
 * A persisted maintenance assignment (junction: request ↔ service provider) as the
 * staff request-detail page consumes it. Own-column field set + nullability mirror
 * the Supabase generated `maintenance_assignments` Row; the nested `provider`
 * relation is appended by the assignments read (loose + optional + nullable).
 */
export const maintenanceAssignmentSchema = z
  .object({
    id: z.string(),
    company_id: z.string(),
    request_id: z.string(),
    provider_id: z.string().nullable(),
    assigned_at: z.string(),
    scheduled_date: z.string().nullable(),
    estimated_cost: z.number().nullable(),
    actual_cost: z.number().nullable(),
    completion_date: z.string().nullable(),
    notes: z.string().nullable(),
    created_at: z.string(),
    updated_at: z.string(),
    // Joined relation (staff assignments read) — loose + optional + nullable.
    provider: relation,
  })
  .passthrough();

export type MaintenanceAssignment = z.infer<typeof maintenanceAssignmentSchema>;
